"use client";

import { useEffect, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  value: number;
  display: string;
  prefix: string;
  suffix: string;
  unit: string;
  description: string;
  decimals: number;
};
const stats: Stat[] = [{
  value: 1250,
  display: "1,250",
  prefix: "",
  suffix: "+",
  unit: "LBS TOTAL LIFTED",
  description: "Cumulative weight moved by our athletes in training.",
  decimals: 0
}, {
  value: 450,
  display: "450",
  prefix: "",
  suffix: "",
  unit: "TRANSFORMATIONS",
  description: "Lives fundamentally altered through our protocols.",
  decimals: 0
}, {
  value: 98,
  display: "98",
  prefix: "",
  suffix: "%",
  unit: "RETENTION RATE",
  description: "Athletes who commit to the Peak Force standard.",
  decimals: 0
}, {
  value: 24,
  display: "24",
  prefix: "",
  suffix: "/7",
  unit: "SUPPORT ACCESS",
  description: "Constant guidance for your peak performance journey.",
  decimals: 0
}];
function AnimatedNumber({
  stat,
  start,
  index
}: {
  stat: Stat;
  start: boolean;
  index: number;
}) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    const duration = 1600;
    const startTime = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(stat.value * eased);
      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      } else {
        setValue(stat.value);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [start, stat.value]);
  const formatted = () => {
    if (stat.decimals === 0 && stat.suffix !== "M") {
      return Math.round(value).toLocaleString("en-US");
    }
    if (stat.suffix === "M") {
      return value.toFixed(stat.decimals);
    }
    return value.toFixed(stat.decimals);
  };
  return <span data-index={index} className="font-default tabular-nums">
      {stat.prefix}
      {formatted()}
      {stat.suffix}
    </span>;
}
export default function ForceByTheNumbers() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <section data-section-id="4537" id="force-by-the-numbers" className="relative overflow-hidden bg-background text-foreground ![padding:100px_0px_200px_0px]">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{
      backgroundImage: "repeating-radial-gradient(circle at 30% 40%, hsl(75 100% 55%) 0, hsl(75 100% 55%) 1px, transparent 1px, transparent 60px), repeating-radial-gradient(circle at 75% 60%, hsl(75 100% 55%) 0, hsl(75 100% 55%) 1px, transparent 1px, transparent 80px)",
      animation: "nourishDrift 40s ease-in-out infinite alternate"
    }} />

      <style>{`
        @keyframes nourishDrift {
          0% { transform: translate3d(0, 0, 0) scale(1); }
          100% { transform: translate3d(-20px, 15px, 0) scale(1.05); }
        }
      `}</style>

      <div ref={ref} className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {}
        <div className={`flex justify-center transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          
        </div>

        {}
        <h2 className={`mt-10 text-center font-default font-bold tracking-tight text-foreground transition-all duration-1000 delay-150 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
        fontSize: "clamp(2.5rem, 6vw, 4.5rem)",
        lineHeight: "1.05"
      }}>
          <span className="block">Quantifiable</span>
          <span className="block italic text-primary">Results.</span>
        </h2>

        {}
        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-16 lg:gap-y-0">
          {stats.map((stat, i) => <div key={i} data-index={i} className={`relative px-2 md:px-6 lg:px-8 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
          transitionDelay: `${300 + i * 150}ms`
        }}>
              {}
              {i > 0 && <span aria-hidden="true" className="hidden lg:block absolute left-0 top-2 bottom-2 w-px bg-border" />}

              {}
              <div className="font-default text-foreground leading-none" style={{
            fontSize: "4.5rem"
          }}>
                <AnimatedNumber stat={stat} start={isVisible} index={i} />
              </div>

              {}
              <div className="mt-6 font-mono text-[11px] md:text-xs tracking-[0.2em] text-primary uppercase leading-relaxed">
                {stat.unit}
              </div>

              {}
              <p className="mt-4 font-default text-sm leading-relaxed text-muted-foreground max-w-[18rem]">
                {stat.description}
              </p>
            </div>)}
        </div>

        {}
        <div className={`mt-20 md:mt-28 flex justify-end transition-all duration-1000 delay-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <span className="font-mono text-[10px] md:text-xs tracking-[0.25em] text-muted-foreground/60 uppercase">Verified by Peak Force Analytics — 2026</span>
        </div>
      </div>
    </section>;
}